import { useEffect, useState } from 'react';
import { getRegistrations, getAllEvents } from '../../services/apiClient';
import type { Registration, Event } from '../../types';
import { formatDateTime } from '../../lib/formatting';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { EmptyState } from '../../components/ui/EmptyState';
import { DataTable, Column } from '../components/DataTable';
import styles from './ManageRegistrations.module.css';

export function ManageRegistrations() {
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [events, setEvents] = useState<Record<string, Event>>({});
  const [eventList, setEventList] = useState<Event[]>([]);
  const [filterEventId, setFilterEventId] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getRegistrations(), getAllEvents()]).then(([regs, evts]) => {
      if (regs.data) setRegistrations(regs.data);
      if (evts.data) {
        const map: Record<string, Event> = {};
        evts.data.forEach((e) => (map[e.id] = e));
        setEvents(map);
        setEventList(evts.data);
      }
      setLoading(false);
    });
  }, []);

  const filtered = registrations.filter((r) => {
    const eventMatch = filterEventId === 'all' || r.event_id === filterEventId;
    const statusMatch = filterStatus === 'all' || r.registration_status === filterStatus;
    return eventMatch && statusMatch;
  });

  const columns: Column<Registration>[] = [
    {
      key: 'name',
      label: 'Registrant',
      render: (reg) => (
        <div>
          <p className={styles.name}>{reg.name}</p>
          <p className={styles.email}>{reg.email}</p>
          {reg.phone && <p className={styles.email}>{reg.phone}</p>}
        </div>
      ),
    },
    {
      key: 'event_id',
      label: 'Event',
      render: (reg) => (
        <span className={styles.eventName}>
          {events[reg.event_id]?.title ?? reg.event_id}
        </span>
      ),
    },
    {
      key: 'team_name',
      label: 'Team',
      render: (reg) => reg.team_name || <span className={styles.na}>—</span>,
    },
    {
      key: 'registration_status',
      label: 'Status',
      render: (reg) => <StatusBadge label={reg.registration_status} />,
    },
    {
      key: 'payment_status',
      label: 'Payment',
      render: (reg) => <StatusBadge label={reg.payment_status} />,
    },
    {
      key: 'notes',
      label: 'Notes',
      render: (reg) => reg.notes
        ? <span className={styles.notes} title={reg.notes}>{reg.notes.slice(0, 40)}{reg.notes.length > 40 ? '…' : ''}</span>
        : <span className={styles.na}>—</span>,
    },
    {
      key: 'created_at',
      label: 'Registered',
      render: (reg) => <span className={styles.date}>{formatDateTime(reg.created_at)}</span>,
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <select
            className={styles.filter}
            value={filterEventId}
            onChange={(e) => setFilterEventId(e.target.value)}
          >
            <option value="all">All Events</option>
            {eventList.map((evt) => (
              <option key={evt.id} value={evt.id}>{evt.title}</option>
            ))}
          </select>

          <select
            className={styles.filter}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="waitlisted">Waitlisted</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <p className={styles.count}>
          {filtered.length} of {registrations.length} registration{registrations.length !== 1 ? 's' : ''}
        </p>
      </div>

      {loading ? (
        <p className={styles.loading}>Loading…</p>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No registrations found"
          description={registrations.length > 0 ? 'Try adjusting the filters.' : 'Registrations will appear here once people sign up for events.'}
        />
      ) : (
        <DataTable columns={columns} rows={filtered} keyField="id" />
      )}
    </div>
  );
}
